import * as XLSX from 'xlsx';
import moment from 'moment';

// Helper function to strip HTML tags from text
const stripHtmlTags = (text) => {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '').trim();
};

// Helper function to capitalize first letter
function capitalizeFirstLetter(str) {
  if (!str) return "N/A";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Helper function to format date
function formatDateMoment(date) {
  if (!date || !moment(date).isValid()) {
    return '-';
  }
  return moment(date).format('DD/MM/YYYY');
}

// Helper function to format date from YYYY-MM-DD
const formatDate = (dateString) => {
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Helper function to group data by patient
const groupDataByPatient = (data) => {
  const groupedData = {};

  data.forEach((item) => {
    const patientKey = `${item.subject.patient_id}_${item.subject.first_name}_${item.subject.last_name}`;

    if (!groupedData[patientKey]) {
      groupedData[patientKey] = {
        subject: item.subject,
        appointments: []
      };
    }

    groupedData[patientKey].appointments.push(...item.appointment);
  });

  return Object.values(groupedData);
};

// Helper function to determine the highest approval status for a visit
const getDataReviewStatus = (approvedUsers) => {
  if (!approvedUsers || approvedUsers.length === 0) {
    return 'Data Entered';
  }
  
  const hasCPM = approvedUsers.some(user => 
    user.includes('Clinical Phase Manager')
  );
  if (hasCPM) {
    return 'Data Approved by CPM';
  }
  
  const hasDQA = approvedUsers.some(user => 
    user.includes('Quality Associate')
  );
  if (hasDQA) {
    return 'Data Approved by DQA';
  }
  
  const hasPI = approvedUsers.some(user => 
    user.includes('Principal Investigator')
  );
  if (hasPI) {
    return 'Data Approved by PI';
  }
  
  return 'Data Entered';
};

// Helper function to group appointments by visit type
const groupAppointmentsByVisitType = (appointments) => {
  const grouped = {};
  
  appointments.forEach((appointment) => {
    const visitType = appointment.form_title || 'Unknown';
    if (!grouped[visitType]) {
      grouped[visitType] = [];
    }
    grouped[visitType].push(appointment);
  });
  
  return grouped;
};

// Variable for Data Review Status dropdown options
const DATA_REVIEW_STATUS_OPTIONS = [
  'Data Entered',
  'Data Approved by PI',
  'Data Approved by DQA',
  'Data Approved by CPM'
];

// Helper function to get questions by visit type
const getQuestionsByVisitType = (data) => {
  const questionsByVisit = {};
  
  data.forEach((item) => {
    item.appointment.forEach((appointment) => {
      const visitType = appointment.form_title || 'Unknown';
      if (!questionsByVisit[visitType]) {
        questionsByVisit[visitType] = new Set();
      }
      
      appointment.reports.forEach((report) => {
        questionsByVisit[visitType].add(stripHtmlTags(report.question));
      });
    });
  });
  Object.keys(questionsByVisit).forEach(visitType => {
    questionsByVisit[visitType] = Array.from(questionsByVisit[visitType]);
  });
  
  return questionsByVisit;
};

// Export to Excel with dropdowns
export const exportToExcelWithDropdowns = (data, filename) => {
  const groupedData = groupDataByPatient(data);
  data.forEach((item, index) => {
    console.log(`Patient ${index + 1}: ${item.subject.patient_id}`);
    item.appointment.forEach((apt) => {
      console.log(`  Visit: ${apt.form_title}`);
      console.log(`  Approved Users:`, apt.approved_users);
      console.log(`  Status:`, getDataReviewStatus(apt.approved_users));
    });
  });
  
  console.log('=== Starting Header Generation ===');

  const staticHeaders = [
    'S.No.',
    'Patient ID',
    'First Name',
    'Last Name',
    'Patient Status',
    'Status Change'
  ];

  // Variable to Get all unique visit types
  const allVisitTypes = new Set();
  data.forEach((item) => {
    item.appointment.forEach((appointment) => {
      if (appointment.form_title) {
        allVisitTypes.add(appointment.form_title);
      }
    });
  });
  const visitTypesArray = Array.from(allVisitTypes);

  // Variable to Get questions organized by visit type
  const questionsByVisit = getQuestionsByVisitType(data);

  const dynamicHeaders = [];
  visitTypesArray.forEach(visitType => {
    const questions = questionsByVisit[visitType] || [];
    questions.forEach(question => {
      dynamicHeaders.push(question);
    });
    dynamicHeaders.push(`${visitType} - Data Review Status`);
  });

  const endingHeaders = [
    'Visit Types',
    'Appointment Titles',
    'Open Queries',
    'Pending Approval',
    'Approved',
  ];

  const allHeaders = [...staticHeaders, ...dynamicHeaders, ...endingHeaders];

  const rows = [];
  let serialNumber = 1;

  groupedData.forEach((patientData) => {
    const row = {};
    
    row['S.No.'] = serialNumber++;
    row['Patient ID'] = patientData.subject.patient_id || "-";
    row['First Name'] = patientData.subject.first_name;
    row['Last Name'] = patientData.subject.last_name;
    row['Patient Status'] = capitalizeFirstLetter(patientData.subject.life_status);
    row['Status Change'] = formatDateMoment(patientData.subject.status_change_date);

    const appointmentsByVisit = groupAppointmentsByVisitType(patientData.appointments);

    const visitTypes = [];
    const appointmentTitles = [];
    const allQueries = [];
    const allPendingUsers = [];
    const allApprovedUsers = [];

    patientData.appointments.forEach((appointment) => {
      if (appointment.form_title) visitTypes.push(appointment.form_title);
      if (appointment.title) appointmentTitles.push(appointment.title);
      if (appointment.query_raised?.length > 0) allQueries.push(...appointment.query_raised);
      if (appointment.pending_users?.length > 0) allPendingUsers.push(...appointment.pending_users);
      if (appointment.approved_users?.length > 0) allApprovedUsers.push(...appointment.approved_users);
    });

    visitTypesArray.forEach(visitType => {
      const questions = questionsByVisit[visitType] || [];
      const visitsOfType = appointmentsByVisit[visitType] || [];
      
      // Variable to Collect answers for this visit type
      const answersForVisit = {};
      visitsOfType.forEach(appointment => {
        appointment.reports.forEach((report) => {
          const question = stripHtmlTags(report.question);
          let answer = report?.answer || '';

          if (typeof answer === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(answer)) {
            answer = formatDate(answer);
          }

          if (Array.isArray(answer)) {
            answer = answer.filter(Boolean).join(', ');
          } else if (answer === null || answer === "") {
            answer = '';
          } else {
            answer = String(answer).replace(/\n/g, ' ');
          }

          if (answer && answer.trim() !== '') {
            if (!answersForVisit[question]) {
              answersForVisit[question] = [];
            }
            answersForVisit[question].push(answer);
          }
        });
      });

      // ForEach loop to Add answers for each question of this visit type
      questions.forEach(question => {
        const answers = answersForVisit[question] || [];
        const uniqueAnswers = [...new Set(answers)];
        row[question] = uniqueAnswers.length > 0 ? uniqueAnswers.join(' | ') : '';
      });

      // Variable to Add Data Review Status for this visit type
      const allApprovedForVisit = [];
      visitsOfType.forEach(visit => {
        if (visit.approved_users?.length > 0) {
          allApprovedForVisit.push(...visit.approved_users);
        }
      });
      const status = getDataReviewStatus(allApprovedForVisit);
      row[`${visitType} - Data Review Status`] = status;
    });

    row['Visit Types'] = visitTypes.length > 0 ? [...new Set(visitTypes)].join(' , ') : 'N/A';
    row['Appointment Titles'] = appointmentTitles.length > 0 ? [...new Set(appointmentTitles)].join(' , ') : 'N/A';
    row['Open Queries'] = allQueries.length > 0 ? [...new Set(allQueries)].join(' , ') : 'N/A';
    row['Pending Approval'] = allPendingUsers.length > 0 ? [...new Set(allPendingUsers)].join(' , ') : 'N/A';
    row['Approved'] = allApprovedUsers.length > 0 ? [...new Set(allApprovedUsers)].join(' , ') : 'N/A';

    rows.push(row);
  });

  const worksheet = XLSX.utils.json_to_sheet(rows, { header: allHeaders });

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  
  const statusColumnIndices = [];
  allHeaders.forEach((header, index) => {
    if (header.includes('- Data Review Status')) {
      statusColumnIndices.push(index);
    }
  });

  // Conditional rendering to Add data validation for each status column
  if (!worksheet['!dataValidation']) {
    worksheet['!dataValidation'] = [];
  }

  statusColumnIndices.forEach(colIndex => {
    for (let rowIndex = 1; rowIndex <= range.e.r; rowIndex++) {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
      
      worksheet['!dataValidation'].push({
        sqref: cellAddress,
        type: 'list',
        formula1: `"${DATA_REVIEW_STATUS_OPTIONS.join(',')}"`,
        showDropDown: true,
        allowBlank: false
      });
    }
  });

  // Variable to Set column widths
  const colWidths = allHeaders.map(header => {
    if (header.includes('- Data Review Status')) {
      return { wch: 25 };
    } else if (header.length > 20) {
      return { wch: 30 };
    } else {
      return { wch: 15 };
    }
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Patient Data');

  XLSX.writeFile(workbook, filename);
};

export const exportToExcel = (data, filename) => {
  const groupedData = groupDataByPatient(data);

  const staticHeaders = [
    'S.No.',
    'Patient ID',
    'First Name',
    'Last Name',
    'Patient Status',
    'Status Change'
  ];

  // Variable to Get all unique visit types
  const allVisitTypes = new Set();
  data.forEach((item) => {
    item.appointment.forEach((appointment) => {
      if (appointment.form_title) {
        allVisitTypes.add(appointment.form_title);
      }
    });
  });
  const visitTypesArray = Array.from(allVisitTypes);

  // Variable to Get questions organized by visit type
  const questionsByVisit = getQuestionsByVisitType(data);

  const dynamicHeaders = [];
  visitTypesArray.forEach(visitType => {
    const questions = questionsByVisit[visitType] || [];
    questions.forEach(question => {
      dynamicHeaders.push(question);
    });
    dynamicHeaders.push(`${visitType} - Data Review Status`);
  });

  const endingHeaders = [
    'Visit Types',
    'Appointment Titles',
    'Open Queries',
    'Pending Approval',
    'Approved',
  ];

  const allHeaders = [...staticHeaders, ...dynamicHeaders, ...endingHeaders];

  const rows = [];
  let serialNumber = 1;

  groupedData.forEach((patientData) => {
    const row = {};
    
    row['S.No.'] = serialNumber++;
    row['Patient ID'] = patientData.subject.patient_id || "-";
    row['First Name'] = patientData.subject.first_name;
    row['Last Name'] = patientData.subject.last_name;
    row['Patient Status'] = capitalizeFirstLetter(patientData.subject.life_status);
    row['Status Change'] = formatDateMoment(patientData.subject.status_change_date);

    const appointmentsByVisit = groupAppointmentsByVisitType(patientData.appointments);

    const visitTypes = [];
    const appointmentTitles = [];
    const allQueries = [];
    const allPendingUsers = [];
    const allApprovedUsers = [];

    patientData.appointments.forEach((appointment) => {
      if (appointment.form_title) visitTypes.push(appointment.form_title);
      if (appointment.title) appointmentTitles.push(appointment.title);
      if (appointment.query_raised?.length > 0) allQueries.push(...appointment.query_raised);
      if (appointment.pending_users?.length > 0) allPendingUsers.push(...appointment.pending_users);
      if (appointment.approved_users?.length > 0) allApprovedUsers.push(...appointment.approved_users);
    });

    visitTypesArray.forEach(visitType => {
      const questions = questionsByVisit[visitType] || [];
      const visitsOfType = appointmentsByVisit[visitType] || [];
      
      const answersForVisit = {};
      visitsOfType.forEach(appointment => {
        appointment.reports.forEach((report) => {
          const question = stripHtmlTags(report.question);
          let answer = report?.answer || '';

          if (typeof answer === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(answer)) {
            answer = formatDate(answer);
          }

          if (Array.isArray(answer)) {
            answer = answer.filter(Boolean).join(', ');
          } else if (answer === null || answer === "") {
            answer = '';
          } else {
            answer = String(answer).replace(/\n/g, ' ');
          }

          if (answer && answer.trim() !== '') {
            if (!answersForVisit[question]) {
              answersForVisit[question] = [];
            }
            answersForVisit[question].push(answer);
          }
        });
      });

      // ForEach loop to Add answers for each question of this visit type
      questions.forEach(question => {
        const answers = answersForVisit[question] || [];
        const uniqueAnswers = [...new Set(answers)];
        row[question] = uniqueAnswers.length > 0 ? uniqueAnswers.join(' | ') : '';
      });

      // Variable to Add Data Review Status for this visit type
      const allApprovedForVisit = [];
      visitsOfType.forEach(visit => {
        if (visit.approved_users?.length > 0) {
          allApprovedForVisit.push(...visit.approved_users);
        }
      });
      const status = getDataReviewStatus(allApprovedForVisit);
      row[`${visitType} - Data Review Status`] = status;
    });

    row['Visit Types'] = visitTypes.length > 0 ? [...new Set(visitTypes)].join(' | ') : 'N/A';
    row['Appointment Titles'] = appointmentTitles.length > 0 ? [...new Set(appointmentTitles)].join(' | ') : 'N/A';
    row['Open Queries'] = allQueries.length > 0 ? [...new Set(allQueries)].join(' | ') : 'N/A';
    row['Pending Approval'] = allPendingUsers.length > 0 ? [...new Set(allPendingUsers)].join(' | ') : 'N/A';
    row['Approved'] = allApprovedUsers.length > 0 ? [...new Set(allApprovedUsers)].join(' | ') : 'N/A';

    rows.push(row);
  });

  const worksheet = XLSX.utils.json_to_sheet(rows, { header: allHeaders });

  const colWidths = allHeaders.map(header => {
    if (header.includes('- Data Review Status')) {
      return { wch: 25 };
    } else if (header.length > 20) {
      return { wch: 30 };
    } else {
      return { wch: 15 };
    }
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Patient Data');

  XLSX.writeFile(workbook, filename);
};
